/**
 * Interface for the 'EventTickets' data
 */

import { BaseEventTicketCheckInEntity } from 'libs/shared/event-tickets/data-access-event-ticket-check-ins/src/lib/+state/base-event-ticket-check-ins.models'
import { BaseSlimEventTicketPackageEntity } from 'libs/shared/models/event-tickets/event-ticket-packages.models'
import { BaseCustomFieldsEntity, VerifiedStateEnum } from 'libs/shared/models/shared.models'

export const eventTicketsEntityName = 'EventTickets'

export interface BaseEventTicketEntity {
  uuid: string
  auction: string
  html_url: string
  pdf_url: string
  purchase: string
  purchaser: EventTicketPurchaserEntity
  purchase_date: string
  profile: EventTicketProfileEntity | null
  guest_status: EventTicketGuestStatusEnum | null
  seat_number: number
  table_number: string | null
  ticket_number: string
  ticket_package: BaseSlimEventTicketPackageEntity
  transferred_to: string | null
  checkin_qrcode: string | null
  notListLoaded?: boolean
}

export interface EventTicketPurchaserEntity {
  uuid: string
  first_name: string
  last_name: string
  email: string
  email_verified_state: VerifiedStateEnum
  user: boolean
}

export interface EventTicketProfileEntity {
  uuid: string
  first_name: string
  last_name: string
  email: string
  email_verified_state: VerifiedStateEnum
  phone_number?: string
  phone_number_verified_state: VerifiedStateEnum
  custom_values: EventTicketCustomValueEntity[]
  required_custom_fields_complete: boolean | null
  user: boolean
}

export interface EventTicketProfilePayload {
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  phone_number?: string
  password?: string
  table_number?: string | null
}

// TODO: consolidate below
export interface EventTicketCustomValue {
  custom_field: string
  value: string
}

export interface EventTicketCustomValueEntity {
  auction: string
  created: string
  custom_field: EventTicketCustomFieldEntity
  profile: string
  ticket_package: string
  uuid: string
  value: string
}
//
export interface EventTicketCustomFieldEntity extends BaseCustomFieldsEntity {
  applied_to_all_ticket_packages: boolean
  default_value: string
  choices: string[]
}

export interface EventTicketGuestStepperPayload {
  profile?: EventTicketProfilePayload
  isSelfAssigned?: boolean
  password?: string
  customValues?: EventTicketCustomValue[]
}

export interface EventTicketGuestStepperConfig {
  title: string
  buttonLabel: string
  nextButtonLabel?: string
  skipLinkLabel?: string
  shouldPrepopulateForm?: boolean
  shouldShowSelfAssign?: boolean
  canEditEmail?: boolean
  shouldShowCancelButton?: boolean
  showDynamicStep?: boolean
  dynamicStepLabel?: string
  emitOnStepSubmit?: boolean
}

export interface AssignEventTicketPayload extends EventTicketProfilePayload {
  custom_values?: EventTicketCustomValue[]
  instance?: string
  self_assign?: boolean
}

export interface EditEventTicketPayload extends EventTicketProfilePayload {
  custom_values?: EventTicketCustomValue[]
}

export interface EventTicketResponse {
  event_ticket: BaseEventTicketEntity
  custom_values?: EventTicketCustomValueEntity[]
}

export interface BulkAssignEventTicketsResponse {
  invites: {
    custom_values: EventTicketCustomValueEntity[]
    event_ticket: BaseEventTicketEntity[]
  }
}

export interface TransferEventTicketPayload extends EventTicketProfilePayload {
  custom_values?: EventTicketCustomValue[]
  self_assign?: boolean
}

export interface TransferEventTicketResponse {
  old_ticket: BaseEventTicketEntity
  new_ticket: BaseEventTicketEntity
}

export interface EventTicketCheckInResponse {
  checkin: BaseEventTicketCheckInEntity
  event_ticket: BaseEventTicketEntity
}

export enum EventTicketGuestStatusEnum {
  Accepted = 'accepted',
  Complete = 'complete',
  CheckedIn = 'checked_in',
  Declined = 'declined',
  Cancelled = 'cancelled',
  InviteSent = 'invite_sent', // TODO (GB-8304): API to be updated to return 'invited' instead of 'invite_sent' for event ticket
  Invited = 'invited',
  None = 'none',
}

export interface EventGuestSummary {
  auction: string
  [EventTicketGuestStatusEnum.CheckedIn]: number
  [EventTicketGuestStatusEnum.Complete]: number
  [EventTicketGuestStatusEnum.Accepted]: number
  [EventTicketGuestStatusEnum.Invited]: number
  [EventTicketGuestStatusEnum.InviteSent]: number // TODO (GB-8304): placeholder until API is updated
  [EventTicketGuestStatusEnum.None]: number
  [EventTicketGuestStatusEnum.Declined]: number
  [EventTicketGuestStatusEnum.Cancelled]: number
}

export enum CheckInGuestResultEnum {
  Success = 'success',
  Failure = 'failure',
}

export interface EventTicketFilters {
  search?: string
  ordering?: string
  guest_status?: (string | null)[]
  profile?: string
}

export enum EventTicketFilterKeyEnum {
  Ordering = 'ordering',
  GuestStatus = 'guest_status',
}

export enum EventTicketDialogResultEnum {
  Success = 'success',
  Cancel = 'cancel',
  None = 'none',
}

export const EVENT_GUESTS_DEFAULT_LIMIT = 10
export const EVENT_GUESTS_DEFAULT_OFFSET = 0
